import React, { useCallback, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { SectionTitle } from './SectionTitle';
import { previewImages, product } from '../data/salesCopy';
export function PreviewSection() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const scrollToIndex = useCallback((next: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(previewImages.length - 1, next));
    const slide = track.children[clamped] as HTMLElement | undefined;
    if (!slide) return;
    track.scrollTo({
      left: slide.offsetLeft - track.offsetLeft,
      behavior: 'smooth'
    });
    setIndex(clamped);
  }, []);
  const handleScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const slideWidth = (track.children[0] as HTMLElement)?.offsetWidth ?? 1;
    setIndex(Math.round(track.scrollLeft / (slideWidth + 12)));
  };
  return <section className="bg-brand-taupe px-4 py-8 text-brand-ivory sm:py-10" data-id="element-188" data-mp="2crzpqffmjxd1">
      <div className="mx-auto max-w-5xl" data-id="element-189" data-mp="2cu42lmvi46pg">
        <SectionTitle light data-id="element-190" data-mp="27tg3edx4cjk5">Preview the Material</SectionTitle>

        <p className="mt-3 text-center font-serif text-lg font-extrabold uppercase leading-tight text-brand-sand sm:text-2xl" data-id="element-191" data-mp="27v9a1t7qf3s5">
          Take a Look Inside {product.name}
        </p>
        <p className="mx-auto mt-2 max-w-2xl text-center text-[13px] font-medium leading-snug text-brand-ivory/85 sm:text-[15px]" data-id="element-192" data-mp="2gue68svtml15">
          Explore the visual pose maps, variation comparisons, prop and
          modification references, sequencing pathways, hold-range guidance and
          ready-to-use teaching resources included throughout the complete
          system.
        </p>

        <div className="relative mt-5" data-id="element-193" data-mp="1y1k44zjaqnec">
          <div ref={trackRef} onScroll={handleScroll} className="flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" tabIndex={0} role="group" aria-roledescription="carousel" aria-label={`Inside ${product.name}`} data-id="element-194" data-mp="1adiypzhezn35">
            {previewImages.map((image, i) => <figure key={image.src} className="w-[86%] shrink-0 snap-center sm:w-[62%] lg:w-[46%]" aria-label={`Page ${i + 1} of ${previewImages.length}`} data-id="element-195" data-mp="3f68o3crqdlb3" data-mpi={i}>
                <div className="overflow-hidden rounded-lg border-2 border-brand-gold/50 bg-white shadow-2xl" data-id="element-196" data-mp="2mj4mlhrujp42">
                  <img src={image.src} alt={image.alt} className="h-auto w-full" loading={i === 0 ? 'eager' : 'lazy'} data-id="element-197" data-mp="2rh696cohe6ka" />
                </div>
              </figure>)}
          </div>

          <button type="button" onClick={() => scrollToIndex(index - 1)} disabled={index === 0} aria-label="Previous page" className="absolute -left-1 top-1/2 -translate-y-1/2 rounded-full border border-brand-gold/60 bg-brand-sage p-2 text-brand-ivory shadow-lg transition-[background-color,opacity] duration-150 ease-smooth hover:bg-brand-sageDark disabled:opacity-30 sm:left-2" data-id="element-198" data-mp="bp8jwn0y9usm">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-199" data-mp="1mi9evauq2mbh" />
          </button>
          <button type="button" onClick={() => scrollToIndex(index + 1)} disabled={index >= previewImages.length - 1} aria-label="Next page" className="absolute -right-1 top-1/2 -translate-y-1/2 rounded-full border border-brand-gold/60 bg-brand-sage p-2 text-brand-ivory shadow-lg transition-[background-color,opacity] duration-150 ease-smooth hover:bg-brand-sageDark disabled:opacity-30 sm:right-2" data-id="element-200" data-mp="1hiwqglo7d76s">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-201" data-mp="32u6kvqpanyoq" />
          </button>
        </div>

        <div className="mt-3 flex items-center justify-center gap-1.5" data-id="element-202" data-mp="2xv3dhzludgdl">
          {previewImages.map((image, i) => <button key={image.src} type="button" onClick={() => scrollToIndex(i)} aria-label={`Go to page ${i + 1}`} aria-current={i === index} className={`h-2 rounded-full transition-[width,background-color] duration-150 ease-smooth ${i === index ? 'w-6 bg-brand-gold' : 'w-2 bg-brand-ivory/40 hover:bg-brand-ivory/70'}`} data-id="element-203" data-mp="1zld81tym23lv" data-mpi={i} />)}
        </div>
      </div>
    </section>;
}